import { createContext, useContext, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Maximize2, X } from 'lucide-react';
import { Button, IconButton } from '@/ui';

/** Records a detail panel can show. */
export type PanelKind = 'ride' | 'vehicle' | 'violation' | 'ticket';
export interface PanelRef {
  kind: PanelKind;
  id: number;
}

/** The URL parameter holding the open panel ("ride-901"), so a panel can be linked and reloaded. */
export const PANEL_PARAM = 'panel';
const KINDS: PanelKind[] = ['ride', 'vehicle', 'violation', 'ticket'];

export function parsePanel(value: string | null): PanelRef | null {
  const m = /^([a-z]+)-(\d+)$/.exec(value ?? '');
  if (!m || !KINDS.includes(m[1] as PanelKind)) return null;
  const id = Number(m[2]);
  return id > 0 ? { kind: m[1] as PanelKind, id } : null;
}

export const formatPanel = (p: PanelRef) => `${p.kind}-${p.id}`;

/**
 * Set by a page that hosts a detail panel (the customer page): ride, vehicle, violation and
 * ticket links inside it open the panel instead of leaving the page, so support keeps the
 * customer in view while checking a ride, its vehicle or a ticket.
 */
export interface PanelNav {
  /** Link target opening `ref` in the panel (keeps the page's other URL parameters). */
  href(ref: PanelRef): string;
  /** What the host page is ("Leo Li", "Rides"): a full page opened from the panel offers a way back to it. */
  origin?: string;
}

/**
 * Router state a panel's "Full page" link carries: the page it came from (with ?panel=, so going
 * back reopens the panel) and its name. PageHeader offers Restore panel, returning to the original source URL.
 */
export interface BackTo {
  path: string;
  label: string;
}
const PanelNavContext = createContext<PanelNav | null>(null);
export const PanelNavProvider = PanelNavContext.Provider;
export const usePanelNav = () => useContext(PanelNavContext);

/** Inside a panel: how to close it and where the record's own page is. */
interface PanelFrame {
  onClose(): void;
}
const PanelFrameContext = createContext<PanelFrame | null>(null);
export const PanelFrameProvider = PanelFrameContext.Provider;

/**
 * Detail screens render either as a page (header with breadcrumbs, roomy grids) or inside a
 * panel (compact header, one column). `inPanel` tells them which.
 */
export const useInPanel = () => useContext(PanelFrameContext) !== null;

/** A panel's header: title and badges, the record's actions, a link to its full page, close. */
export function PanelHeader({ title, badges, actions, fullPage }: { title: ReactNode; badges?: ReactNode; actions?: ReactNode; fullPage?: string }) {
  const { t } = useTranslation();
  const frame = useContext(PanelFrameContext);
  const nav = usePanelNav();
  const location = useLocation();
  const backTo: BackTo = { path: location.pathname + location.search + location.hash, label: nav?.origin ?? t('panel.previousPage') };
  return (
    <header className="flex flex-col gap-2 border-b border-line px-4 py-3">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="m-0 font-display text-lg font-semibold text-heading">{title}</h2>
          {badges}
        </div>
        {fullPage && (
          <Button asChild variant="ghost" size="sm">
            <Link to={fullPage} state={{ backTo }}>
              <Maximize2 aria-hidden />
              {t('panel.fullPage')}
            </Link>
          </Button>
        )}
        {frame && <IconButton label={t('actions.close')} icon={<X />} size="sm" onClick={frame.onClose} />}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
